import { useState } from "react";
import { useLocalStorage } from "hooks/use-local-storage";
import { useStore } from "hooks/use-store";
import RadioGroup from "components/ui/radio-group";
import classNames from "classnames";
import { Gauge, Grid2X2, LineChart, Settings2 } from "lucide-react";
import { RpmSogToggle } from "components/rpm-sog-toggle";
import { SeaSicknessAlert } from "components/sea-sickness-alert";
import { useWeather } from "hooks/use-weather";
import { PLACEHOLDER } from "utils/constants";
import { Sheet } from "components/ui/sheet";
import { DashboardMode } from "types/enums";
import { Color } from "utils/theme";
import { Button } from "components/ui/button";
import { GuidanceModeCard } from "components/guidance-mode-card";

interface ToolbarProps {
  className?: string | Record<string, boolean>;
}

export const Toolbar = ({ className }: ToolbarProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const weather = useWeather();

  const [, setLocalDashboardMode] = useLocalStorage<DashboardMode>(
    "dashboard_mode",
    DashboardMode.Gauges
  );
  const [, setLocalSafetySpeedEnabled] = useLocalStorage<boolean>(
    "safety_speed_enabled",
    true
  );
  const [, setLocalEcoSpeedEnabled] = useLocalStorage<boolean>(
    "eco_speed_enabled",
    true
  );

  const dashboardMode = useStore((state) => state.dashboardMode);
  const duration = useStore((state) => state.duration);
  const distance = useStore((state) => state.distance);
  const featureFlags = useStore((state) => state.featureFlags);

  // This is sourced from the Eco speed program itself, so it indicates whether we have that data available or not
  const hasEcoSog = useStore((state) => state.hasEcoSog);

  const setDashboardMode = useStore((state) => state.setDashboardMode);

  const onDashboardModeChange = (mode: DashboardMode) => {
    setLocalDashboardMode(mode);
    setDashboardMode(mode);
  };

  const seaState = weather?.data?.sea_state?.value ?? PLACEHOLDER;

  const safetySpeedEnabled = useStore((state) => state.safetySpeedEnabled);
  const ecoSpeedEnabled = useStore((state) => state.ecoSpeedEnabled);
  const setSafetySpeedEnabled = useStore(
    (state) => state.setSafetySpeedEnabled
  );
  const setEcoSpeedEnabled = useStore((state) => state.setEcoSpeedEnabled);

  return (
    <>
      <div
        className={classNames(
          "hidden sm:flex items-center justify-between py-3 px-4 space-x-2 overflow-hidden shrink-0",
          className
        )}
      >
        <section className="flex-1 flex items-center space-x-5 overflow-hidden">
          <div className="flex items-baseline space-x-2 shrink-0">
            <span className="text-xl lg:text-2xl font-semibold tabular-nums">
              {duration}
            </span>
            <span
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: Color.Primary[400] }}
            >
              Duration
            </span>
          </div>
          <div className="flex items-baseline space-x-2 shrink-0">
            <span className="text-xl lg:text-2xl font-semibold tabular-nums">
              {distance}
            </span>
            <span
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: Color.Primary[400] }}
            >
              nmi
            </span>
          </div>
          <div className="flex items-baseline space-x-2 shrink-0">
            <span className="text-xl lg:text-2xl font-semibold tabular-nums">
              {seaState}
            </span>
            <span
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: Color.Primary[400] }}
            >
              Sea state
            </span>
          </div>
          {featureFlags.seaSickness && <SeaSicknessAlert />}
        </section>
        <section className="flex justify-end">
          <Button
            variant="secondary"
            onClick={() => setSheetOpen(true)}
            aria-label="Open settings"
            data-analytics="toolbar.dashboard-settings"
          >
            <Settings2 className="w-5 h-5 mr-2" color={Color.Primary[400]} />
            <span className="text-lg">Settings</span>
          </Button>
        </section>
      </div>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Dashboard Settings"
      >
        <section className="space-y-8">
          <div className="space-y-3">
            <span
              className="text-base font-medium uppercase tracking-widest"
              style={{ color: Color.Primary[400] }}
            >
              Guidance Mode
            </span>
            <div>
              <GuidanceModeCard
                title="SafetySpeed"
                description="Speed limit based on real-time conditions."
                enabled={safetySpeedEnabled}
                onToggle={(v) => {
                  setLocalSafetySpeedEnabled(v);
                  setSafetySpeedEnabled(v);
                }}
                featureFlag={!featureFlags.safetySpeed}
              />
              <GuidanceModeCard
                title="EcoSpeed"
                description={
                  hasEcoSog
                    ? "Eco-friendly speed ranges for fuel efficiency."
                    : "EcoSpeed model not available."
                }
                enabled={ecoSpeedEnabled}
                onToggle={(v) => {
                  setLocalEcoSpeedEnabled(v);
                  setEcoSpeedEnabled(v);
                }}
                featureFlag={!featureFlags.ecoSpeed}
              />
            </div>
          </div>

          <div className="space-y-3">
            <span
              className="text-base font-medium uppercase tracking-widest"
              style={{ color: Color.Primary[400] }}
            >
              Dashboard View
            </span>
            <RadioGroup
              items={[
                {
                  label: (
                    <div className="flex items-center">
                      <Gauge className="w-7 h-7 mr-2" />
                      <span className="text-xl">Gauges</span>
                    </div>
                  ),
                  value: DashboardMode.Gauges,
                },
                {
                  label: (
                    <div className="flex items-center">
                      <Grid2X2 className="w-7 h-7 mr-2" />
                      <span className="text-xl">Blocks</span>
                    </div>
                  ),
                  value: DashboardMode.Blocks,
                },
                ...(hasEcoSog
                  ? [
                      {
                        label: (
                          <div className="flex items-center">
                            <LineChart className="w-7 h-7 mr-2" />
                            <span className="text-xl">Graph</span>
                          </div>
                        ),
                        value: DashboardMode.Chart,
                      },
                    ]
                  : []),
              ]}
              selected={dashboardMode}
              setSelected={onDashboardModeChange}
              name="dashboard-view"
            />
          </div>

          <div className="space-y-3">
            <span
              className="text-base font-medium uppercase tracking-widest"
              style={{ color: Color.Primary[400] }}
            >
              Eco Gauge Metric
            </span>
            <RpmSogToggle />
          </div>
        </section>
      </Sheet>
    </>
  );
};
